import {
  createRouter,
  type StaticDataRouteOption,
} from "@tanstack/react-router"

import { nightshiftRewrite } from "@/lib/terminal/nightshift-domain"
import { shellModeOf, shellTransitionTypes } from "@/lib/terminal/shell-mode"

import { routeTree } from "./routeTree.gen"

function NotFound() {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <p className="text-muted-foreground">
        The page you're looking for doesn't exist.
      </p>
    </div>
  )
}

// A navigation that changes the shell's screen is one view transition, typed
// (`shellTransitionTypes`) so its CSS (terminal-theme.css) applies to it
// alone; every other navigation changes the screen at once. A browser that
// cannot type a transition gets none, rather than a crossfade on every
// navigation.
type ViewTransition = Parameters<
  typeof createRouter
>[0]["defaultViewTransition"]

// Reads each location's screen from its routes (`shellModeOf`), so it needs
// the router it configures.
type MatchRoutes = (
  pathname: string
) => readonly { staticData?: StaticDataRouteOption }[]

const shellViewTransition = (matchRoutes: MatchRoutes): ViewTransition =>
  typeof document !== "undefined" &&
  window.CSS?.supports?.("selector(:active-view-transition-type(a))")
    ? {
        types: ({ fromLocation, toLocation }) =>
          // Reduced motion: the screen changes at once.
          fromLocation &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? shellTransitionTypes(
                shellModeOf(matchRoutes(fromLocation.pathname)),
                shellModeOf(matchRoutes(toLocation.pathname))
              )
            : false,
      }
    : false

export function getRouter() {
  // Called only on navigation, after the router exists.
  const matchRoutes: MatchRoutes = (pathname) => router.matchRoutes(pathname)
  const router = createRouter({
    routeTree,
    rewrite: nightshiftRewrite,
    // The anonymous website preserves a reader's position while its hero
    // hydrates. Authenticated Terminal keeps normal router restoration.
    scrollRestoration: ({ location }) => {
      if (location.pathname !== "/terminal") return true
      if (typeof document === "undefined") return false
      return document.querySelector("[data-terminal-website]") === null
    },
    // Start loaders on link hover/touchstart so navigation feels instant.
    defaultPreload: "intent",
    // Reuse loader data for 1 minute instead of refetching on every
    // navigation. Mutations still see fresh data via router.invalidate().
    defaultStaleTime: 60_000,
    defaultPreloadStaleTime: 60_000,
    defaultNotFoundComponent: NotFound,
    defaultViewTransition: shellViewTransition(matchRoutes),
    context: {
      user: null,
      userType: null,
    },
  })

  return router
}

declare module "@tanstack/react-router" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- TanStack Router's Register contract requires declaration merging.
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
